/* eslint-disable max-params */
import {
  LOCAL_STORAGE_KEY,
  NOTIFICATION_TYPE,
  SYSTEM_CONSTANT,
  USER_GROUP,
  USER_ROLE,
} from "constants/common.constant";
import { AxiosError } from "axios";
import { TypeCommon } from "models/common";
import moment from "moment";
import { ROUTERS } from "constants/routes.constant";
import { jwtDecode } from "jwt-decode";
import Resizer from "react-image-file-resizer";
import { getAccessJwtToken } from "api/base";
import { toast } from "react-toastify";

export const isJsonObject = (strData: TypeCommon): boolean => {
  try {
    JSON.parse(strData);
  } catch (e) {
    return false;
  }
  return true;
};

const TOAST_MAP: Record<string, typeof toast.info> = {
  [NOTIFICATION_TYPE.INFO]: toast.info,
  [NOTIFICATION_TYPE.ERROR]: toast.error,
  [NOTIFICATION_TYPE.SUCCESS]: toast.success,
  [NOTIFICATION_TYPE.WARNING]: toast.warning,
};

export const ToastMessage = (
  type: string,
  messTitle: string,
  description = "",
): void => {
  const content = isJsonObject(messTitle)
    ? JSON.stringify(messTitle)
    : messTitle;
  const displayContent = description ? `${content} - ${description}` : content;
  const showToast = TOAST_MAP[type] ?? toast.info;
  showToast(displayContent, {
    position: "top-right",
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  });
};

export const handleError = (
  error: AxiosError<TypeCommon>,
  rejectWithValue: (value: unknown) => TypeCommon,
): TypeCommon => {
  const err: AxiosError<TypeCommon> = error;
  if (!err.response) {
    throw err;
  }
  return rejectWithValue(err.response.data);
};

export const formatDate = (date: TypeCommon, format?: string): string => {
  if (!date || !moment(date).isValid()) {
    return "";
  }
  return moment(new Date(date)).format(
    format ?? SYSTEM_CONSTANT.SYSTEM_LOCAL_DATE_FORMAT,
  );
};

export const addSuffixtoString = (): TypeCommon => {
  const date = new Date();
  return (
    date.getFullYear() +
    ("0" + (date.getMonth() + 1)).slice(-2) +
    ("0" + date.getDate()).slice(-2) +
    ("0" + date.getHours()).slice(-2) +
    ("0" + date.getMinutes()).slice(-2) +
    ("0" + date.getSeconds()).slice(-2)
  );
};

export const checkToken = async (): Promise<void> => {
  const token = await getAccessJwtToken();
  if (token) {
    const { exp } = jwtDecode(token) as TypeCommon;
    const expirationTime = exp * 1000 - 60000;
    if (Date.now() >= expirationTime) {
      removeLocalDataIgnoreKey([LOCAL_STORAGE_KEY.LIST_USER]);
      sessionStorage.clear();
      window.location.assign(ROUTERS.LOGIN);
    }
  } else if (!token && window.location.pathname !== ROUTERS.LOGIN) {
    removeLocalDataIgnoreKey([LOCAL_STORAGE_KEY.LIST_USER]);
    sessionStorage.clear();
    window.location.assign(ROUTERS.LOGIN);
  }
};

/**
 * GIF/APNG/WebP など動きのある画像かどうかを判定する。
 * リサイズ(JPEG変換)すると静止画になってしまうため、圧縮対象から除外する。
 */
export const isAnimatedImage = (file: TypeCommon): boolean => {
  const type = file?.type?.toLowerCase() || "";
  const name = file?.name?.toLowerCase() || "";
  return (
    ["image/gif", "image/apng", "image/webp"].includes(type) ||
    /\.(gif|apng|webp)$/.test(name)
  );
};

export const resizeFile = (
  file: TypeCommon,
  width?: number,
  height?: number,
): TypeCommon =>
  new Promise((resolve) => {
    Resizer.imageFileResizer(
      file,
      width || 1000,
      height || 1000,
      "JPEG",
      100,
      0,
      (uri) => {
        resolve(uri);
      },
      "file",
    );
  });

export const generateUserRole = (user: TypeCommon): string => {
  let role = "";
  switch (user?.group) {
    case USER_GROUP.PLATFORM_ADMIN: {
      if (user?.role === USER_ROLE.ROOT) role = "プラットフォームルート";
      else if (user?.role === USER_ROLE.MANAGER)
        role = "プラットフォーム管理者";
      else role = "プラットフォーム運用者";
      break;
    }
    case USER_GROUP.ADMIN: {
      if (user?.role === USER_ROLE.ROOT) role = "サービスルート";
      else if (user?.role === USER_ROLE.MANAGER) role = "サービス管理者";
      else role = "サービス運用者";
      break;
    }
    case USER_GROUP.APPROVER: {
      if (user?.role === USER_ROLE.ROOT) role = "承認ルート";
      else if (user?.role === USER_ROLE.MANAGER) role = "承認管理者";
      else role = "一般承認者";
      break;
    }
    default: {
      if (user?.role === USER_ROLE.ROOT) role = "申請ルート";
      else if (user?.role === USER_ROLE.MANAGER) role = "申請管理者";
      else role = "一般申請者";
      break;
    }
  }
  return role;
};

export const removeLocalDataIgnoreKey = (keepKeys: string[]): void => {
  Object.keys(localStorage).forEach((key) => {
    if (!keepKeys?.includes(key)) {
      localStorage.removeItem(key);
    }
  });
};

export const fetchTokens = async (code: string): Promise<boolean> => {
  try {
    const body = new URLSearchParams({
      grant_type: "authorization_code",
      client_id: "ke60sjpuo295i99e9j2n594q0",
      code,
      redirect_uri: "https://staging.summitgco.com/service",
    });

    const response = await fetch(
      "https://ap-northeast-16rlejhmhi.auth.ap-northeast-1.amazoncognito.com/oauth2/token",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: body.toString(),
      },
    );

    const data = await response.json();

    if (data.id_token) {
      const decoded: TypeCommon = jwtDecode(data.id_token);

      // 2. Lưu thông tin vào localStorage
      localStorage.setItem("accessToken", data.access_token);
      localStorage.setItem("idToken", data.id_token);
      localStorage.setItem("refreshToken", data.refresh_token);
      localStorage.setItem("user", JSON.stringify(decoded));
      localStorage.setItem("loginProvider", "AzureAD");
      return true;
    }
    return false;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("Error exchanging code for tokens", err);
    return false;
  }
};

export const getIdTokenFromLocalStorage = (): TypeCommon => {
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (key && key.includes(LOCAL_STORAGE_KEY.ID_TOKEN)) {
      return localStorage.getItem(key);
    }
  }
  return null;
};

export const getRefreshTokenFromLocalStorage = (): TypeCommon => {
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (key && key.includes(LOCAL_STORAGE_KEY.REFRESH_TOKEN_AWS)) {
      return localStorage.getItem(key);
    }
  }
  return null;
};
